import { Link as RouterLink } from 'react-router-dom';
import { Link as ChakraLink } from "@chakra-ui/react";
import { Button, Flex, Text, Box, Menu as ChakraMenu, MenuButton, MenuList, MenuItem } from "@chakra-ui/react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { ReactComponent as ArrowDownBlack } from 'assets/arrow-down-black-icon.svg';
import { ReactComponent as PlusIcon } from 'assets/plus-icon.svg';
import { GlobalContext } from '../../../providers/Global';
import { ModalType, SCREEN_LG, SCREEN_SM } from "utils/constants";
import { ReactPortal } from 'hooks/usePortal';

interface IMobileMenuProps {
  pageName: string;
  backUrl?: string;
  onAction?: () => void;
}

export const MobileMenu = (props: IMobileMenuProps) => {
  const { pageName, backUrl, onAction } = props;
  const { openModal } = useContext(GlobalContext);

  const refContent = useRef<HTMLDivElement | null>(null);
  const refContainer = useRef<HTMLDivElement | null>(null);

  const [isSticky, setIsSticky] = useState(false);

  const isScreenSmall = useMediaQuery({ query: `(max-width: ${SCREEN_SM})` });
  const isScreenLarge = useMediaQuery({ query: `(max-width: ${SCREEN_LG})` });

  const handleScroll = useCallback(() => {
    if (!isScreenSmall) {
      return;
    }

    setIsSticky(!!(refContent.current?.scrollTop));
  }, [isScreenSmall]);

  useEffect(() => {
    const el = document.getElementById('content') as HTMLDivElement;

    if (el) {
      refContent.current = el as HTMLDivElement;
      refContent.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (refContent.current) {
        refContent.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [handleScroll]);

  return (
    <ReactPortal
      id="content"
      withHeader
      disablePortal={!isScreenSmall}
    >
      <Flex
        ref={refContainer}
        justifyContent="space-between"
        alignItems="center"
        px={isScreenSmall ? 4 : 0}
        bg={isSticky ? 'white' : ''}
        boxShadow={isSticky ? 'md' : 'none'}
        top={isScreenSmall ? 0 : 'auto'}
        position={isScreenSmall ? 'sticky' : 'unset'}
        transition="box-shadow 0.3s ease-in-out"
        mt={isScreenSmall ? 0 : 2}
        mb={isScreenSmall ? -2 : 2}
      >
        {
          isScreenLarge && !!backUrl?.length && (
            <ChakraLink
              as={RouterLink}
              to={backUrl}
              w={12}
              h={12}
              pl={isScreenSmall ? 3 : 0}
              ml={!isScreenSmall ? -3 : 0}
              display="flex"
              justifyContent="center"
              alignItems="center"
              textDecoration="none !important"
            >
              <Box transform="rotate(90deg)">
                <ArrowDownBlack />
              </Box>
            </ChakraLink>
          )
        }
        <Text
          size={isScreenSmall ? 'md' : "2xl"}
          variant="bodyBold"
        >
          {pageName}
        </Text>

        {pageName === 'Address Book' ? (
          <Button
            variant="primary"
            borderRadius={6}
            onClick={onAction}
            size="md"
          >
            Add Contact
          </Button>
        ) : isScreenLarge && (pageName === 'Transactions' || pageName === 'Dashboard') ? (
          <ChakraMenu placement="bottom-end">
            <MenuButton
              as={Flex}
              alignItems="center"
              gap={1}
              px={1}
              py={1}
              borderRadius={6}
              cursor="pointer"
              title="Create transaction"
              transition="background 0.15s"
              _hover={{ bg: 'blackAlpha.100' }}
            >
              <PlusIcon style={{ width: 18, height: 18 }} />
              <Text fontSize="xs" fontWeight="500">Create</Text>
            </MenuButton>
            <MenuList minW="180px" py={2} borderRadius={8} boxShadow="lg" zIndex={10}>
              <MenuItem
                fontSize="sm"
                py={2}
                fontWeight="600"
                onClick={() => openModal(ModalType.CreateTransactionWizard)}
              >
                New Transaction...
              </MenuItem>
              <MenuItem
                fontSize="sm"
                py={2}
                onClick={() => openModal(ModalType.CreateTransactionWizard, { presetType: 'SEND_TOKENS' })}
              >
                Send tokens
              </MenuItem>
              <MenuItem
                fontSize="sm"
                py={2}
                onClick={() => openModal(ModalType.CreateTransactionWizard, { presetType: 'TOKEN_ASSOCIATE' })}
              >
                Add a token
              </MenuItem>
              <MenuItem
                fontSize="sm"
                py={2}
                onClick={() => openModal(ModalType.CreateTransactionWizard, { presetType: 'CONTRACT_INTERACTION' })}
              >
                Build a transaction
              </MenuItem>
            </MenuList>
          </ChakraMenu>
        ) : null}
      </Flex>
    </ReactPortal >
  )
}

MobileMenu.displayName = 'MobileMenu';
